// Catch-all gate for staff who can't reach the wizard admin tools. Two
// distinct cases land here, with different copy:
//
//   1. No active grant (Phase 2b). The user is staff in the dashboard but a
//      super_admin hasn't ticked "Self-Serve Admin Access" on their row in
//      Settings → Users. Action: contact account manager to flip the toggle.
//
//   2. Grant exists, but no stations match. The grant is on, yet
//      /stations/my-admin returned []. This happens when the user's
//      assigned-station list (org_admin scope, or manager/sales_rep's
//      user_metadata.stations[]) doesn't resolve to any rows in
//      public.stations that have self_service_enabled = true. Most common
//      reason: the wizard catalog hasn't been provisioned for those
//      station names yet. Action: contact account manager to onboard the
//      stations.
//
// Sole CTA: sign out → /admin. The user can't act here themselves either way.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';

const AccessDeniedPage: React.FC = () => {
  const { user, signOut, hasPublisherAdminGrant, myAdminStations } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin', { replace: true });
  };

  // Differentiate "grant missing" vs "grant on but no stations resolved."
  // Default to the grant-missing copy when state is still loading so a
  // brief race doesn't accidentally show the wrong message.
  const grantOn = hasPublisherAdminGrant === true;
  const stationsResolved = myAdminStations !== null;
  const noStations = grantOn && stationsResolved && myAdminStations.length === 0;

  const emailLabel = user?.email ?? 'Your account';

  let title: string;
  let body: string;
  if (noStations) {
    title = 'No stations are set up for your account';
    body = `${emailLabel} has self-serve admin access enabled, but none of the stations assigned to your account have been onboarded to the self-serve system yet. Contact your CTV Buyer account manager to provision your stations.`;
  } else {
    title = 'Self-serve admin access required';
    body = `${user?.email ? `${user.email} doesn't have access` : "Your account doesn't have access"} to the self-serve admin tools yet. Contact your CTV Buyer account manager to enable this feature.`;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center">
        <ShieldAlert className="w-10 h-10 text-amber-600 mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-slate-900 mb-2">{title}</h1>
        <p className="text-sm text-slate-600 mb-6">{body}</p>
        <button
          type="button"
          onClick={handleSignOut}
          className="inline-flex items-center justify-center px-5 py-2.5 rounded-md text-sm font-semibold text-white bg-slate-900 hover:bg-slate-700"
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

export default AccessDeniedPage;
